<template>
	<BasePlaceHolder
		:line-count="5"
		:section-count="2"
		:placehold="loading"
		:class="{ 'col-4-md col-offset-4-md mt-600-md px-100-smo pt-300-smo': loading }"
	>
		<BaseLayout
			:background-color-class="backgroundColorClass"
			:content-full-height="true"
			:compact-mobile-padding="true"
			data-test="base-layout"
		>
			<template #header>
				<HeaderUnified
					v-if="displayTestVariant"
					classes="bg-color-white"
					cancel-click-text="Icon-close"
					:cancel-link="{ router: 'home' }"
					data-test="header-unified"
				>
					<template #back>
						<BaseLink
							v-if="isOrderReview"
							:link="{ router: 'checkout-acknowledgments' }"
							class="text-color-content-primary display-flex align-items-center header-back-link"
						>
							<BaseSvgIcon name="chevron" dir="left" width="24" height="24" />
							<span :class="appStore.isMobile ? 'body-sm' : 'body-md'">Back</span>
						</BaseLink>

						<BaseLink
							v-else-if="isEntityAccountDetailsStep"
							:link="{ router: 'checkout-account-type' }"
							class="text-color-content-primary display-flex align-items-center header-back-link"
						>
							<BaseSvgIcon name="chevron" dir="left" width="24" height="24" />
							<span :class="appStore.isMobile ? 'body-sm' : 'body-md'">Back</span>
						</BaseLink>

						<CheckoutBackButton
							v-else-if="!shouldHideBackButton"
							:disabled="isBackButtonDisabled"
							:display-icon="true"
							:mixpanel-custom-properties="signupStore.mixpanelCustomProperties"
							classes="text-color-content-primary display-flex align-items-center"
						/>
					</template>
				</HeaderUnified>
				<HeaderBasic v-else :cancel-modal="cancelModal" />
			</template>

			<section :class="['container', { grid: !displayTestVariant }]">
				<div
					:class="
						displayTestVariant && !appStore.isMobile ? 'signup-module' : 'col-12 col-6-md col-offset-3-md'
					"
					data-test="signup-module"
				>
					<slot>
						<router-view v-slot="{ Component }">
							<TransitionSubpage>
								<component :is="Component" />
								<template #bottom-content> </template>
							</TransitionSubpage>
						</router-view>
					</slot>
				</div>
			</section>
		</BaseLayout>
	</BasePlaceHolder>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import CancelConfirmationModal from '@components/header/cancel-confirmation-modal.vue';
import CheckoutBackButton from '@components/checkout/checkout-back-button.vue';
import HeaderBasic from '@components/header/header-basic.vue';
import HeaderUnified from '@components/header/header-unified.vue';
import TransitionSubpage from '@components/layout/transition-subpage.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useSignupStore } from '@stores/signup';

const route = useRoute();

const appStore = useAppStore();
const signupStore = useSignupStore();

const cancelModal = {
	modal: CancelConfirmationModal,
	props: {
		redirect: 'home'
	}
};
const isInTest = ref(false);

const loading = ref(true);

const isBackButtonDisabled = computed(() => {
	return signupStore.isBackButtonDisabled;
});

const shouldHideBackButton = computed((): boolean => {
	const unsupportedBackRoutes = ['checkout-personal-info', 'checkout-password'];

	const name = route.name;

	if (typeof name !== 'string') {
		return true;
	}

	return unsupportedBackRoutes.includes(name);
});

const backgroundColorClass = computed((): string => {
	return displayTestVariant.value && !appStore.isMobile ? 'bg-color-bg-secondary' : 'bg-color-white';
});

const displayTestVariant = computed((): boolean => {
	return isInTest.value && !route.path.includes('ira');
});

const isOrderReview = computed((): boolean => route.name === 'checkout-review-order');

const isEntityAccountDetailsStep = computed((): boolean => {
	const name = route.name;

	if (typeof name !== 'string') {
		return false;
	}

	return (
		name.includes('checkout-entity-account-details') ||
		name.includes('checkout-joint-account-details') ||
		name.includes('checkout-trust-account-details')
	);
});

async function setup(): Promise<void> {
	useHead({
		title: 'Open Your Account',
		meta: [{ property: 'og:title', content: 'Open Your Account' }]
	});

	try {
		isInTest.value = await signupStore.determineStreamlineSignupTestGroup();
	} catch {
	} finally {
		loading.value = false;
	}
}

setup();
</script>

<style scoped>
:deep(a.header-back-link:hover),
:deep(a.header-back-link:active),
:deep(a.header-back-link:focus) {
	color: inherit;
}
</style>
